import { useLocation } from 'react-router'
import { ButtonLink } from '../../platform/primitives/ButtonLink'
import './errors.css'

/**
 * The 404 screen — rendered by `App.tsx`'s catch-all `*` route, INSIDE
 * `ChromeLayout`. That placement is deliberate (see `App.tsx`'s route-table
 * doc comment: "a 404 with no navigation is a dead end") and it changes what
 * this file draws relative to the comp it transcribes:
 *
 * **No full-viewport centring.** The comp wraps everything in
 * `min-height:100vh; display:flex; align-items:center; justify-content:center`
 * — a standalone page. Here the sidebar and top bar already own the viewport,
 * `.shell__main` already supplies the content-area inset, and this component
 * is one more thing rendered inside it, like `Security` or `StatesReference`.
 * Re-centring inside that box would float the message in the middle of
 * whatever the content area happens to be tall, which nothing else in the
 * chrome does.
 *
 * **Starts at `<h2>`.** `ChromeLayout` renders the page name as the document's
 * only `<h1>` — here that resolves to "Not found" (`ChromeLayout`'s
 * `item?.label ?? 'Not found'`) — so a second, page-titling heading here would
 * give the document two. Same rule `Security.tsx` and `StatesReference.tsx`
 * follow.
 *
 * **The badge is `--accent-soft`/`--accent-ink`, not the comp's
 * `repeating-linear-gradient(135deg,var(--accent) 0 6px,#C7E356 6px 12px)`.**
 * Two separate reasons, not one:
 *
 * - Rule 1 — `#C7E356` is a literal hex outside `tokens.css`, which the repo
 *   forbids regardless of what else is true.
 * - Rule 9 — even a flat `var(--accent)` fill would be a SECOND full-strength
 *   lime fill in this content area; "Back to Overview" below is already the
 *   one rule 9 budgets. `--accent-soft` is exempt from that budget the same
 *   way `.auth-icon` in `auth.css` is exempt on the MFA and Verify-device
 *   screens — the rule counts the full-strength fill, and this is not it.
 *
 * **The trailing path is real, not the comp's placeholder text.** The comp
 * hardcodes `/household/9f2/reports/quarterly-2024`; this reads
 * `useLocation().pathname`, so it names the link that actually missed.
 *
 * **No `<Async>`.** Nothing here is fetched — there is no loading, empty,
 * stale or error state for a route that does not exist — so the five/four
 * -state contract does not apply, the same way it does not for `SessionExpired`
 * or `Security`'s own static copy.
 */
export function NotFound() {
  const { pathname } = useLocation()

  return (
    <div className="notfound">
      <span className="notfound__badge">404</span>
      <div className="notfound__group">
        <h2 className="notfound__title">This page isn&apos;t in your household.</h2>
        <p className="notfound__note">
          The link may be old, or it may point at an account, budget or report that has since
          been deleted or renamed.
        </p>
      </div>
      {/**
       * `ButtonLink`, not `<Button onClick={navigate}>`.
       *
       * Both of these are DESTINATIONS, and the element has to say so. A
       * `<button>` that navigates announces itself as a control, cannot be
       * middle-clicked or opened in a new tab, has no href for the status bar
       * or for "copy link address", and is invisible to anything that harvests
       * a page's outgoing links. The version this replaced used buttons and the
       * route table's own test caught it: it asserts a LINK to `/overview`,
       * which is the property, not the styling.
       *
       * **This block used to write out `Button`'s class list by hand, and the
       * copy was wrong.** It carried the classes but not the inline
       * `min-height`, so both recoveries measured 42.8px against M0-R29's 44px
       * floor at every phone width — sixteen of the seventeen failures in the
       * hit-target sweep (#334), on `/no-such-route` and again through
       * `/__probe?screen=notfound`. Nothing but a browser sweep could see it:
       * the classes were right, the skin was right, and jsdom does no layout.
       *
       * The docblock that stood here predicted the shape and stopped one copy
       * short — "if a third arrives, that is the argument for teaching `Button`
       * to render an anchor rather than a fourth copy". This WAS the fourth.
       */}
      <div className="notfound__actions">
        <ButtonLink variant="primary" to="/overview">
          Back to Overview
        </ButtonLink>
        <ButtonLink variant="chip" to="/transactions">
          Search transactions
        </ButtonLink>
      </div>
      <span className="notfound__path">{pathname}</span>
    </div>
  )
}
